import { useEffect, useState } from "react";
import * as http from "./http.ts";

type UseBackendDataProps = {
  url: string | null;
  withCredentials?: Boolean;
  afterLoad?: (data: any) => void;
  resetWhileLoading?: Boolean;
};

function useBackendData({
  url,
  withCredentials = false,
  afterLoad,
  resetWhileLoading = true,
}: UseBackendDataProps) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!url) return;
    const controller = new AbortController();
    if (resetWhileLoading) {
      setData(null);
    }
    http
      .getJson(url, {
        credentials: withCredentials ? "include" : "same-origin",
        signal: controller.signal,
      })
      .then((data) => {
        setData(data);
        afterLoad?.(data);
      })
      .catch((error) => {
        if (error.name !== "AbortError") console.error(url, error);
      });
    return () => controller.abort();
  }, [url, withCredentials]);

  return data;
}

type UseFormStateProps = {
  action?: string;
  initialValue?: any;
  validate?: () => Record<string, string> | undefined;
  handleSubmitted?: (data: any) => void;
  onChange?: (newValue: any, name: string | null) => void;
  name?: string | null;
};

function useFormState({
  action,
  initialValue = {},
  validate,
  handleSubmitted,
  onChange,
  name = null,
}: UseFormStateProps) {
  const [value, setValue] = useState(initialValue);
  const [requestState, setRequestState] = useState<http.RequestState>({
    submitting: false,
    error: null,
    errors: {},
    errorData: null,
    info: null,
  });

  function handleChange(newValue, key) {
    if (newValue.target) {
      const event = newValue;
      newValue =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      const parts = event.target.name.split(".");
      key = parts[parts.length - 1];
    }
    setValue((oldValue) => {
      const data = Array.isArray(oldValue) ? [...oldValue] : { ...oldValue };
      data[key] = newValue;
      onChange?.(data, name);
      return data;
    });
  }

  function handleSubmit(event?) {
    event?.preventDefault();
    setRequestState((current) => ({ ...current, submitting: true }));
    const errors = validate?.();
    if (errors && Object.keys(errors).length > 0) {
      setRequestState((current) => ({
        ...current,
        submitting: false,
        errors,
        error: errors.__form__,
      }));
      return;
    }
    if (action) {
      http
        .postJson(action, value)
        .then((data) => {
          setRequestState((current) => ({ ...current, submitting: false }));
          handleSubmitted?.(data);
        })
        .catch((error: http.HttpError) => {
          setRequestState((current) => ({
            ...current,
            ...error.state,
          }));
        });
    } else {
      handleSubmitted?.(value);
    }
  }

  useEffect(() => {
    if (!requestState.errors) return;
    const error = document.querySelector(".validation-error");
    const field = error?.closest(".field");
    if (field) {
      window.scrollTo({
        top: field.getBoundingClientRect().top + window.scrollY,
        behavior: "smooth",
      });
    }
  }, [requestState.errors]);

  return {
    value,
    setValue,
    ...requestState,
    setRequestState,
    handleChange,
    handleSubmit,
  };
}

export { useBackendData, useFormState };
